import { Injectable } from "@angular/core";
import { Subject, BehaviorSubject } from "rxjs";
import Environment from "@vision/core-map-api/src/Environment";
import { IViewerconfiguration } from "../interfaces/i-viewerconfiguration";
import { Viewerlayer } from "../model/viewerlayer";
import { IUrlParams } from "../interfaces/i-url-params";
import { ViewerConstants } from "../../shared/model/viewer-constants";

@Injectable({
  providedIn: "root",
})
export class ViewerConfigurationService {
  private viewerConfigurationSubject = new BehaviorSubject<IViewerconfiguration>(null);

  public viewerConfiguration$ = this.viewerConfigurationSubject.asObservable();

  private tokenSubject = new BehaviorSubject<string>(null);

  public tokenSubject$ = this.tokenSubject.asObservable();

  private optionsSubject = new BehaviorSubject<any>(null);

  public optionsSubject$ = this.optionsSubject.asObservable();

  private initializedMapSubject = new BehaviorSubject<boolean>(false);

  public initializedMapSubject$ = this.initializedMapSubject.asObservable();

  private createMapSubject = new Subject<boolean>();

  public createMap$ = this.createMapSubject.asObservable();

  private preloadLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  public preloadLayers$ = this.preloadLayersSubject.asObservable();

  protected unauthorizedSubject = new Subject<boolean>();

  public unauthorized$ = this.unauthorizedSubject.asObservable();

  protected urlParamsSubject = new BehaviorSubject<IUrlParams>(null);

  public urlParams$ = this.urlParamsSubject.asObservable();

  protected setDefaultLocationSubject = new BehaviorSubject<boolean>(false);

  public setDefaultLocation$ = this.setDefaultLocationSubject.asObservable();

  protected geocodeEnabledSubject = new BehaviorSubject<boolean>(false);

  public geocodeEnabled$ = this.geocodeEnabledSubject.asObservable();

  protected readNotificationsSubject = new BehaviorSubject<string[]>(null);

  public readNotifications$ = this.readNotificationsSubject.asObservable();

  protected fitToWKTSubject = new Subject<string>();

  public fitToWKT$ = this.fitToWKTSubject.asObservable();

  protected embeddingsImplementationSubject = new BehaviorSubject<string>(null);

  public embeddingsImplementation$ = this.embeddingsImplementationSubject.asObservable();

  private globalMeasurementSystemSubject = new BehaviorSubject<number>(ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC);

  public globalMeasurementSystem$ = this.globalMeasurementSystemSubject.asObservable();

  preloadLayers: Viewerlayer[];

  session: string;

  setPreloadLayers(layers: Viewerlayer[]) {
    this.preloadLayers = layers;
    this.preloadLayersSubject.next(layers);
  }

  getPreloadLayers(): Viewerlayer[] {
    return this.preloadLayersSubject.getValue();
  }

  setViewerConfiguration(viewerConf: IViewerconfiguration) {
    Environment.GEOSERVER_URL = viewerConf.geoserverUrl;
    Environment.API_URL = viewerConf.apiUrl;
    Environment.VIEWER_URL = viewerConf.viewerUrl;
    Environment.PRIMARY_COLOR = viewerConf.primaryColor;
    Environment.APP_NAME = viewerConf.appName;
    Environment.APP_KEY = viewerConf.appKey;
    this.viewerConfigurationSubject.next(viewerConf);
  }

  updateInstance(instance: any) {
    const viewerConf = this.viewerConfigurationSubject.getValue();
    if (viewerConf) {
      viewerConf.userExpireDate = null;
      viewerConf.instanceId = instance.id;
      viewerConf.instanceExpireDate = instance.expireDate;
    }
  }

  updateInstanceId(id: any) {
    const viewerConf = this.viewerConfigurationSubject.getValue();
    if (viewerConf && id) {
      viewerConf.instanceId = id;
    }
  }

  updateToken(token: string) {
    const viewerConf = this.viewerConfigurationSubject.getValue();
    if (viewerConf) {
      viewerConf.token = token;
      this.viewerConfigurationSubject.next(viewerConf);
    }
    this.tokenSubject.next(token);
  }

  getViewerConfiguration(): IViewerconfiguration {
    return this.viewerConfigurationSubject.getValue();
  }

  updateOptions(options: any) {
    this.optionsSubject.next(options);
  }

  getOptions(): any {
    return this.optionsSubject.getValue();
  }

  /**
   * Return if configuration its correct
   * @returns {boolean}
   */
  isValid(): boolean {
    return !!(
      this.getViewerConfiguration() &&
      this.getViewerConfiguration().token &&
      this.getViewerConfiguration().geoserverUrl &&
      this.getViewerConfiguration().viewerUrl
    );
  }

  /**
   * Return if map its initialized
   * @returns {boolean}
   */
  isInitialized(): boolean {
    return this.initializedMapSubject.getValue();
  }

  initializedMap(initialize: boolean): void {
    this.initializedMapSubject.next(initialize);
  }

  createMap(): void {
    this.createMapSubject.next(true);
  }

  destroyMap(): void {
    this.setPreloadLayers([]);
    this.viewerConfigurationSubject.next(null);
    this.initializedMapSubject.next(false);
  }

  setUnauthorized() {
    this.unauthorizedSubject.next(true);
  }

  setUrlParams(urlParams: IUrlParams) {
    this.urlParamsSubject.next(urlParams);
  }

  getUrlParams(): IUrlParams {
    return this.urlParamsSubject.getValue();
  }

  setDefaultLocation(goHome: boolean) {
    this.setDefaultLocationSubject.next(goHome);
  }

  getDefaultLocationValue(): boolean {
    return this.setDefaultLocationSubject.getValue();
  }

  setGeocodeEnabled(enabled: boolean) {
    this.geocodeEnabledSubject.next(enabled);
  }

  setReadNotifications(notifications: string[]) {
    this.readNotificationsSubject.next(notifications);
  }

  getReadNotifications(): string[] {
    return this.readNotificationsSubject.getValue();
  }

  setFitToWKT(wkt: string) {
    this.fitToWKTSubject.next(wkt);
  }

  setEmbeddingsImplementation(implementation: string) {
    this.embeddingsImplementationSubject.next(implementation);
  }

  getEmbeddingsImplementation(): string {
    return this.embeddingsImplementationSubject.getValue();
  }

  setGlobalMeasurementSystem(systemId: number) {
    this.globalMeasurementSystemSubject.next(systemId);
  }

  getGlobalMeasurementSystem(): number {
    return this.globalMeasurementSystemSubject.getValue();
  }
}
